import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material';
import { LocationEnvironmentApi } from '@api/location-environment/location-environment.api';
import { LocationSectorApi } from '@api/location-sectors/location-sectors.api';
import { ActionTypeEnum, TreeViewItem } from '@core/components/fc-tree-view/fc-tree.types';
import { LocationSector } from '@core/interfaces/locations-sectors';
import { sortArchivedThenByName } from '@core/utils/sort-archived-then-by-name';
import { TranslateService } from '@ngx-translate/core';
import { DialogEquipmentService } from 'app/main/equipments/dialog/dialog-equipments.service';
import {
  DialogLocationEnvironmentComponent,
  DialogLocationEnvironmentData,
  DialogLocationEnvironmentResult
} from 'app/main/locations/dialogs/dialog-location-environment/dialog-location-environment.component';
import {
  DialogLocationSectorComponent,
  DialogLocationSectorData,
  DialogLocationSectorResult
 } from 'app/main/locations/dialogs/dialog-location-sector/dialog-location-sector.component';
import { Subject } from 'rxjs';
import { Equipment } from '../../../../../../core/interfaces';
import { LocationEnvironment } from '../../../../../../core/interfaces/locations-environments';
import {
  FormatEnvironmentsParams,
  FormatEquipmentsParams,
  FormatUnlinkedEquipments
} from './dialog-link/dialog-link.types';
import {
  LinkSectorsEnvironmentsEquipmentsParams,
  LinkSectorsEnvironmentsEquipmentsResult,
  ReloadItemSubject,
  UnlinkItemSubject,
} from './tab-links.types';

@Injectable({
  providedIn: 'root'
})
export class TabLinksService {
  public unlinkItem$: Subject<UnlinkItemSubject> = new Subject();
  public reloadItem$: Subject<ReloadItemSubject> = new Subject();

  constructor(
    private readonly matDialog: MatDialog,
    private readonly locationSectorApi: LocationSectorApi,
    private readonly locationEnvironmentApi: LocationEnvironmentApi,
    private readonly dialogEquipmentService: DialogEquipmentService,
    private readonly translateService: TranslateService,
  ) {}

  public linkSectorsEnvironmentsEquipments(params: LinkSectorsEnvironmentsEquipmentsParams): LinkSectorsEnvironmentsEquipmentsResult {
    const { sectors, environments, equipments } = params;

    const environmentsBySectorMap: Record<string, LocationEnvironment[]> = {};
    environments.forEach((environment) => {
      environmentsBySectorMap[environment.locationSector.id] = environmentsBySectorMap[environment.locationSector.id] || [];
      environmentsBySectorMap[environment.locationSector.id].push(environment);
    });

    const unlinkedEquipments: Equipment[] = [];
    const equipmentsBySectorMap: Record<string, Equipment[]> = {};
    const equipmentsByEnvironmentMap: Record<string, Equipment[]> = {};

    equipments.forEach((equipment) => {
      if (equipment.locationSector) {
        if (equipment.locationEnvironment) {
          equipmentsByEnvironmentMap[equipment.locationEnvironment.id] = equipmentsByEnvironmentMap[equipment.locationEnvironment.id] || [];
          equipmentsByEnvironmentMap[equipment.locationEnvironment.id].push(equipment);
        } else {
          equipmentsBySectorMap[equipment.locationSector.id] = equipmentsBySectorMap[equipment.locationSector.id] || [];
          equipmentsBySectorMap[equipment.locationSector.id].push(equipment);
        }
      } else {
        unlinkedEquipments.push(equipment);
      }
    });

    sectors.sort(sortArchivedThenByName);
    sectors.forEach((sector) => {
      sector.equipments = equipmentsBySectorMap[sector.id] || [];
      sector.equipments.sort(sortArchivedThenByName);
      Reflect.deleteProperty(equipmentsBySectorMap, sector.id);

      sector.locationEnvironments = environmentsBySectorMap[sector.id] || [];
      sector.locationEnvironments.sort(sortArchivedThenByName);

      sector.locationEnvironments.forEach((environment) => {
        environment.equipments = equipmentsByEnvironmentMap[environment.id] || [];
        Reflect.deleteProperty(equipmentsByEnvironmentMap, environment.id);
        environment.equipments.sort(sortArchivedThenByName);
      });
    });

    Object.values(equipmentsBySectorMap).forEach((equipmentsWithNoSector) => {
      unlinkedEquipments.push(...equipmentsWithNoSector);
    });

    Object.values(equipmentsByEnvironmentMap).forEach((equipmentsWithNoEnvironment) => {
      unlinkedEquipments.push(...equipmentsWithNoEnvironment);
    });

    unlinkedEquipments.sort(sortArchivedThenByName);

    return {
      sectors,
      unlinkedEquipments
    };
  }

  public formatSectors(params: LinkSectorsEnvironmentsEquipmentsParams): TreeViewItem[] {
    const { sectors, maintenance } = params;
    return (sectors || []).map((sector) => {
      const sectorItem: TreeViewItem = {
        id: sector.id,
        text: sector.name,
        description: sector.description,
        archived: sector.archived,
        archivedText: this.translateService.instant('MAINTENANCES.CREATE_DIALOG.TAB_LINKS.ARCHIVED'),
        icon: 'sector',
        metadata: 'sector',
        original: sector,
        action: {
          type: ActionTypeEnum.menu,
          buttons: [
            {
              text: this.translateService.instant('MAINTENANCES.CREATE_DIALOG.TAB_LINKS.MENU.VIEW_SECTOR'),
              icon: 'wysiwyg',
              color: '#676767',
              action: async () => {
                await this.openLocationSectorDialog(sector.id);
              }
            },
            {
              text: this.translateService.instant('MAINTENANCES.CREATE_DIALOG.TAB_LINKS.MENU.UNLINK_SECTOR'),
              icon: 'delete',
              color: '#676767',
              disabled: Boolean(maintenance && maintenance.id),
              action: async () => {
                this.unlinkItem$.next({
                  id: sector.id,
                  formControlName: 'locationSectors'
                });
              }
            }
          ]
        }
      };

      sectorItem.children = [
        ...this.formatEnvironments({
          environments: sector.locationEnvironments,
          parent: sectorItem,
          maintenance
        }),
        ...this.formatEquipments({
          equipments: sector.equipments,
          maintenance,
          parent: sectorItem
        }),
      ];

      return sectorItem;
    });
  }

  public formatUnlinkedEquipments(params: FormatUnlinkedEquipments): TreeViewItem[] {
    const { maintenance, unlinkedEquipments } = params;
    const formattedEquipments = this.formatEquipments({ equipments: unlinkedEquipments, maintenance });

    if (!formattedEquipments.length) {
      return [];
    }

    return [{
      text: this.translateService.instant('MAINTENANCES.CREATE_DIALOG.TAB_LINKS.EQUIPMENTS_WITHOUT_SECTOR_ENVIRONMENT'),
      icon: 'more_vertical',
      children: formattedEquipments,
      action: {
        type: ActionTypeEnum.none
      }
    }];
  }

  private formatEnvironments(params: FormatEnvironmentsParams): TreeViewItem[] {
    const { parent, environments, maintenance } = params;
    return (environments || []).map((environment) => {
      const environmentItem: TreeViewItem =       {
        id: environment.id,
        text: environment.name,
        description: environment.description,
        icon: 'environment',
        original: environment,
        archived: environment.archived,
        archivedText: this.translateService.instant('MAINTENANCES.CREATE_DIALOG.TAB_LINKS.ARCHIVED'),
        parent,
        metadata: 'environment',
        action: {
          type: ActionTypeEnum.menu,
          buttons: [
            {
              text: this.translateService.instant('MAINTENANCES.CREATE_DIALOG.TAB_LINKS.MENU.VIEW_ENVIRONMENT'),
              icon: 'wysiwyg',
              color: '#676767',
              action: async () => {
                await this.openLocationEnvironmentDialog(environment.id);
              }
            },
            {
              text: this.translateService.instant('MAINTENANCES.CREATE_DIALOG.TAB_LINKS.MENU.UNLINK_ENVIRONMENT'),
              icon: 'delete',
              color: '#676767',
              disabled: Boolean(maintenance && maintenance.id),
              action: async () => {
                this.unlinkItem$.next({
                  id: environment.id,
                  formControlName: 'locationEnvironments'
                });
              }
            }
          ]
        }
      };

      environmentItem.children = this.formatEquipments({
        equipments: environment.equipments,
        parent: environmentItem,
        maintenance
      });

      return environmentItem;
    });
  }

  private formatEquipments(params: FormatEquipmentsParams): TreeViewItem[] {
    const { equipments, parent, maintenance } = params;
    return (equipments || []).map((equipment) => ({
      id: equipment.id,
      text: equipment.name,
      description: equipment.number,
      archived: equipment.archived,
      archivedText: this.translateService.instant('MAINTENANCES.CREATE_DIALOG.TAB_LINKS.ARCHIVED'),
      icon: 'equipment',
      image: equipment.imageUrl,
      children: [],
      parent,
      original: equipment,
      metadata: 'equipment',
      action: {
        type: ActionTypeEnum.menu,
        buttons: [
          {
            text: this.translateService.instant('MAINTENANCES.CREATE_DIALOG.TAB_LINKS.MENU.VIEW_EQUIPMENT'),
            icon: 'wysiwyg',
            color: '#676767',
            action: async () => {
              await this.openEquipmentDialog(equipment.id);
            }
          },
          {
            text: this.translateService.instant('MAINTENANCES.CREATE_DIALOG.TAB_LINKS.MENU.UNLINK_EQUIPMENT'),
            icon: 'delete',
            color: '#676767',
            disabled: Boolean(maintenance && maintenance.id),
            action: async () => {
              this.unlinkItem$.next({
                id: equipment.id,
                formControlName: 'equipments'
              });
            }
          }
        ]
      }
    }));
  }

  private async openLocationSectorDialog(locationSectorId: string): Promise<void> {
    const locationSector = await this.locationSectorApi.get(locationSectorId);

    const result = await this.matDialog
      .open<DialogLocationSectorComponent, DialogLocationSectorData, DialogLocationSectorResult>(DialogLocationSectorComponent, {
        panelClass: 'dialog-location-sector',
        data: {
          locationSector: locationSector
        }
      })
      .afterClosed()
      .toPromise();

    if (result && result.reload) {
      const updatedValue = await this.locationSectorApi.get(locationSectorId);
      this.reloadItem$.next({
        updatedValue,
        formControlName: 'locationSectors'
      });
    }
  }

  private async openLocationEnvironmentDialog(locationEnvironmentId: string): Promise<void> {
    const locationEnvironment = await this.locationEnvironmentApi.get(locationEnvironmentId);
    locationEnvironment.location = locationEnvironment.locationSector.location;

    const result = await this.matDialog
      .open<DialogLocationEnvironmentComponent, DialogLocationEnvironmentData, DialogLocationEnvironmentResult>(DialogLocationEnvironmentComponent, {
        panelClass: 'dialog-location-environment',
        data: {
          locationEnvironment,
        }
      })
      .afterClosed()
      .toPromise();

    if (result && result.reload) {
      const updatedValue = await this.locationEnvironmentApi.get(locationEnvironmentId);
      this.reloadItem$.next({
        updatedValue,
        formControlName: 'locationEnvironments'
      });
    }
  }

  private async openEquipmentDialog(equipmentId: string): Promise<void> {
    const result = await this.dialogEquipmentService.open({
      equipmentId,
      disableEditLocation: true
    });

    if (result && result.reload) {
      const updatedValue = await this.dialogEquipmentService.getEquipment({ equipmentId });
      this.reloadItem$.next({
        updatedValue,
        formControlName: 'equipments'
      });
    }
  }

  public filter(items: TreeViewItem[], searchTerm: string): TreeViewItem[] {
    searchTerm = (searchTerm || '').toLowerCase().trim();

    return items.filter((item) => {
      if (item.children) {
        item.children = this.filter(item.children, searchTerm);
      }

      const text = (item.text || '').toLowerCase();
      const description = (item.description || '').toLowerCase();

      return text.includes(searchTerm) || description.includes(searchTerm) || (item.children && item.children.length) || this.parentMatch(item, searchTerm);
    });
  }

  private parentMatch(item: TreeViewItem, searchTerm: string): boolean {
    if (!item.parent) { return false; }

    searchTerm = (searchTerm || '').toLowerCase().trim();

    const text = (item.parent.text || '').toLowerCase();
    const description = (item.parent.description || '').toLowerCase();

    return text.includes(searchTerm) || description.includes(searchTerm) || this.parentMatch(item.parent, searchTerm);
  }

  public sortAllByArchivedThenByName(locationSectors: LocationSector[], unlinkedEquipments: Equipment[]): { sectors: LocationSector[]; unlinkedEquipments: Equipment[] } {
    locationSectors.sort(sortArchivedThenByName);
    locationSectors.forEach((sector) => {
      if (sector.locationEnvironments) {
        sector.locationEnvironments.sort(sortArchivedThenByName);
        sector.locationEnvironments.forEach((environment) => {
          if (environment.equipments) {
            environment.equipments.sort(sortArchivedThenByName);
          }
        });
      }

      if (sector.equipmentsWithoutEnvironment) {
        sector.equipmentsWithoutEnvironment.sort(sortArchivedThenByName);
      }
    });

    unlinkedEquipments.sort(sortArchivedThenByName);

    return {
      sectors: locationSectors,
      unlinkedEquipments: unlinkedEquipments
    };
  }
}
